import React, { useEffect, useRef, useState } from "react";
import { NumericTextBox } from "@progress/kendo-react-inputs";
import { useFormCalculation } from "../../contexts/FormCalculationContext";
import "./NumberRenderer.scss";

const NumberRenderer = ({ element, onChange, showResponses = false, isGridElement }) => {
  const numericTextBoxRef = useRef(null);
  const { properties = {}, value } = element;
  const {
    label = "Number Field",
    placeholder = "Enter number...",
    required = false,
    disabled = false,
    min = null,
    max = null,
    step = 1,
    decimals = 0,
    format = "#",
    spinners = true,
    defaultValue = null,
    onlyPositiveNumbers = false,
    autoSetValue = false,
    workflowVariableId = null,
    showNumberSpinner = false,
    showChipName: showChipNameRaw,
  } = properties;
  const showChipName = showChipNameRaw === false ? false : true;
  const { getCalculationValue } = useFormCalculation();
  const [inputValue, setInputValue] = useState(() => {
    return value && typeof value === "number" ? value : undefined;
  });
  const evaluatedValue = autoSetValue && workflowVariableId ? getCalculationValue(workflowVariableId) : null;
  const displayValue = typeof evaluatedValue === "number" && autoSetValue ? evaluatedValue : value;
  // Determine min value: if onlyPositiveNumbers is true, min should be 0 (or higher)
  const effectiveMin = onlyPositiveNumbers ? (min !== null && min > 0 ? min : 0) : min;
  // Disable the input if it is disabled, show responses, or auto set value
  const isDisabled = disabled || showResponses || autoSetValue;

  // Disable wheel scroll when spinner is disabled
  useEffect(() => {
    if (!showNumberSpinner && numericTextBoxRef.current) {
      const handleWheel = (e) => {
        e.preventDefault();
        e.stopPropagation();
      };
      const element = numericTextBoxRef.current.element || numericTextBoxRef.current;
      const input = element?.querySelector?.("input");
      if (input) {
        input.addEventListener("wheel", handleWheel, { passive: false, capture: true });
        return () => input.removeEventListener("wheel", handleWheel, { capture: true });
      }
    }
  }, [showNumberSpinner]);

  useEffect(() => {
    if(!autoSetValue && typeof value === "number") {
      onChange?.(value);
    }
  }, [])

  const handleChange = (e) => {
    setInputValue(e.target.value || 0 );
    onChange?.(e.target.value || 0 );
  };

  return (
    <div className={`number-renderer ${isGridElement ? "mb-0" : "mb-3"}`}>
      {showChipName && (
        <label htmlFor={`${element?.id}-number`} className='form-label'>
          {element?.name}
        </label>
      )}
      <div className='d-flex'>
        <NumericTextBox
          ref={numericTextBoxRef}
          spinners={showNumberSpinner}
          {...(!showNumberSpinner && {
            onWheel: (e) => {
              e.preventDefault();
              e.target.blur(); // stops value change on scroll
            },
          })}
          placeholder={placeholder}
          required={required}
          defaultValue={defaultValue}
          disabled={isDisabled}
          {...(effectiveMin !== null && { min: effectiveMin })}
          {...(max && { max })}
          format='0'
          onChange={handleChange}
          className='number-input'
          value={autoSetValue ? displayValue : inputValue}
        />
        {required && <span className='required-asterisk text-danger ms-1 ml-1'>*</span>}
      </div>
    </div>
  );
};

export default NumberRenderer;
